import { NavigateFunction } from "react-router-dom"
import { makeAutoObservable, onBecomeObserved } from "mobx"

import { notify } from "@src/app/components/notify"
import { paths } from "@src/app/paths"
import { currenciesApi, projectsApi } from "@src/shared/api"
import { EnabledCrypto } from "@src/shared/api/generated"
import { UploadData } from "@src/shared/form/uploader"
import { WithQuery } from "@src/shared/WithQuery"

export type ProjectLogos = {
  original: string
  small: string
}

type Project = {
  callback_signature_key?: string
  callback_url?: string
  description?: string
  enabled_crypto?: string[]
  image?: UploadData<ProjectLogos>
  logo?: UploadData<ProjectLogos>
  name?: string
  return_url?: string
  status?: "active" | "inactive"
  theme?: any
}

type Currency = {
  decimals: number
  display_name: string
  network: string
  symbol: string
  token_kind: null | string
  tx_explorer_url: string
}

export class ProjectUpsertStore {
  private _currencies: Currency[] = []
  imageCacheId = ""
  isImageRemoved = false
  isLoading = false
  isLogoRemoved = false
  isUpserting = false

  loadCurrencies = async () => {
    try {
      this.isLoading = true

      const { data: result } = await currenciesApi.getCurrencies()

      if (result.success) {
        // @ts-ignore
        this._currencies = result.data
      } else {
        notify.error()
      }
    } catch (error) {
      notify.error()
    } finally {
      this.isLoading = false
    }
  }
  logoCacheId = ""

  project = new WithQuery(projectsApi.getProject.bind(projectsApi), {
    loadOnMount: false,
    onError: () => notify.error(),
    onSuccess: (data) => !data.data.success ?? notify.error(),
  })

  setImageCacheId = (id: string) => {
    this.imageCacheId = id

    this.isImageRemoved = false
  }
  setLogoCacheId = (id: string) => {
    this.logoCacheId = id

    this.isLogoRemoved = false
  }

  upsert = (
    {
      callback_url,
      description,
      enabled_crypto,
      image,
      logo,
      name,
      return_url,
      status,
      theme,
    }: Project,
    id?: string,
  ) => {
    this.isUpserting = true

    const projectInfo: any = {
      callback_url,
      description,
      enabled_crypto: enabled_crypto?.map((token) => {
        const [symbol, network] = token.split("_")

        return { network, symbol } as { network: string; symbol: string }
      }) as EnabledCrypto[],
      // @ts-ignore
      image,
      logo,
      name,
      return_url,
      // @ts-ignore
      status,
      theme,
    }

    return (
      id
        ? projectsApi.updateProject(id, projectInfo)
        : projectsApi.createProject(projectInfo)
    )
      .then(() => {
        if (!id) {
          notify.success("Project created")

          this.navigate(paths.projects)
        } else {
          notify.success("Project updated")

          this.navigate(paths.projects)
        }
      })
      .finally(() => {
        this.isUpserting = false
      })
  }

  constructor(private navigate: NavigateFunction) {
    makeAutoObservable(this)

    onBecomeObserved(this, "_currencies", this.loadCurrencies)
  }

  get currencies() {
    return this._currencies.slice()
  }
}
